import type { OnlineBookingSeasonPeriod } from "@/lib/onlineBooking";
import {
  monthDayInRecurring,
  monthDayIsOvernightInRecurring,
} from "@shared/monthDayInRecurring";

function parseIsoDate(iso: string): { year: number; month: number; day: number } {
  const [y, m, d] = iso.split("-").map(Number);
  return { year: y ?? 0, month: m ?? 1, day: d ?? 1 };
}

export function isDateInBookingSeason(iso: string, season: OnlineBookingSeasonPeriod): boolean {
  const { month, day } = parseIsoDate(iso);
  if (season.mode === "fixed") {
    if (!season.start_date || !season.end_date) return false;
    return iso >= season.start_date && iso <= season.end_date;
  }
  if (
    season.start_month == null ||
    season.start_day == null ||
    season.end_month == null ||
    season.end_day == null
  ) {
    return false;
  }
  return monthDayInRecurring(
    month,
    day,
    season.start_month,
    season.start_day,
    season.end_month,
    season.end_day,
  );
}

/** Buchbare Nacht. Der Saison-Endtag ist nur Abreise, keine Anreise. */
function isOvernightInBookingSeason(iso: string, season: OnlineBookingSeasonPeriod): boolean {
  const { month, day } = parseIsoDate(iso);
  if (season.mode === "fixed") {
    if (!season.start_date || !season.end_date) return false;
    return iso >= season.start_date && iso < season.end_date;
  }
  if (
    season.start_month == null ||
    season.start_day == null ||
    season.end_month == null ||
    season.end_day == null
  ) {
    return false;
  }
  return monthDayIsOvernightInRecurring(
    month,
    day,
    season.start_month,
    season.start_day,
    season.end_month,
    season.end_day,
  );
}

export function isDateBookable(iso: string, seasons: OnlineBookingSeasonPeriod[]): boolean {
  if (seasons.length === 0) return true;
  return seasons.some((season) => isOvernightInBookingSeason(iso, season));
}

function previousIsoDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  const prev = new Date(y ?? 0, (m ?? 1) - 1, (d ?? 1) - 1);
  const ny = prev.getFullYear();
  const nm = String(prev.getMonth() + 1).padStart(2, "0");
  const nd = String(prev.getDate()).padStart(2, "0");
  return `${ny}-${nm}-${nd}`;
}

function toLocalIsoDate(year: number, month: number, day: number): string {
  const m = String(month).padStart(2, "0");
  const d = String(day).padStart(2, "0");
  return `${year}-${m}-${d}`;
}

/** Oberes Datum für die Datumsauswahl innerhalb der konfigurierten Saisons. */
export function latestSelectableSeasonDate(
  seasons: OnlineBookingSeasonPeriod[],
  fromIso: string,
): string | undefined {
  if (seasons.length === 0) return undefined;

  const [fromYear] = fromIso.split("-").map(Number);
  const candidates: string[] = [];

  for (const year of [fromYear ?? 0, (fromYear ?? 0) + 1]) {
    for (const season of seasons) {
      if (season.mode === "fixed") {
        if (!season.end_date) continue;
        const lastNight = previousIsoDate(season.end_date);
        if (lastNight >= fromIso && isOvernightInBookingSeason(lastNight, season)) {
          candidates.push(lastNight);
        }
        continue;
      }
      if (season.end_month == null || season.end_day == null) continue;
      const endIso = toLocalIsoDate(year, season.end_month, season.end_day);
      const lastNight = previousIsoDate(endIso);
      if (lastNight >= fromIso && isOvernightInBookingSeason(lastNight, season)) {
        candidates.push(lastNight);
      }
    }
  }

  return candidates.sort().at(-1);
}

export function soldOutDateSelectionError(
  iso: string,
  seasons: OnlineBookingSeasonPeriod[],
  todayIso: string,
): string | null {
  if (!iso) return null;
  if (iso < todayIso) {
    return "Nur zukünftige Nächte können eingetragen werden.";
  }
  if (!isDateBookable(iso, seasons)) {
    if (seasons.length === 0) return null;
    if (seasons.some((season) => isDateInBookingSeason(iso, season))) {
      return "An diesem Tag ist nur die Abreise möglich.";
    }
    const labels = seasons
      .slice(0, 3)
      .map((season) => season.name)
      .filter(Boolean)
      .join(", ");
    const suffix = seasons.length > 3 ? " …" : "";
    return `Das Datum liegt außerhalb der Buchungssaison${labels ? ` (${labels}${suffix})` : ""}.`;
  }
  return null;
}
